import React from 'react';
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SharedResultLink } from '@/types/sharing/sharing.types';
import { setChartAnimationsDisabled } from '@/utils/chartAnimation';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import enTranslations from '@/locales/en.json';
import ptTranslations from '@/locales/pt.json';

/**
 * Translation helper function
 */
const getTranslation = (language: 'en' | 'pt', key: string): string => {
  const translations = language === 'en' ? enTranslations : ptTranslations;
  const keys = key.split('.');
  let value: any = translations;

  for (const k of keys) {
    if (value === undefined) return key;
    value = value[k];
  }

  return value || key;
};

/**
 * Delay execution for specified milliseconds
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Wait until the recharts shapes (pie sectors / bar rectangles) have rendered
 * and their count is stable, then let the enter animations finish. A blind
 * fixed delay races against lazy imports + Firestore data loading, which
 * intermittently captured charts before anything was drawn (empty pies/bars
 * in the exported PDF).
 */
const waitForChartsToRender = async (root: HTMLElement, timeoutMs = 10000): Promise<void> => {
  const start = Date.now();
  let previousCount = -1;
  while (Date.now() - start < timeoutMs) {
    const count = root.querySelectorAll('.recharts-sector, .recharts-bar-rectangle').length;
    if (count > 0 && count === previousCount) break;
    previousCount = count;
    await delay(500);
  }
  // Shapes appear at animation start; wait out the longest enter animation
  // (1000ms on the pie charts) so the final geometry is what gets captured.
  await delay(1500);
};

/**
 * Wrapper component to set the language in the provider
 */
const LanguageWrapper: React.FC<{ language: 'en' | 'pt'; children: React.ReactNode }> = ({ language, children }) => {
  const { setLanguage } = useLanguage();

  React.useEffect(() => {
    setLanguage(language);
  }, [language, setLanguage]);

  return <>{children}</>;
};

/**
 * Mock SharedResults component for PDF rendering
 * We'll render a simplified version that matches the web layout
 */
interface SharedResultsContentProps {
  link: SharedResultLink;
  language: 'en' | 'pt';
  companyId: string;
  companyName: string;
  productName: string;
  year: number;
  footprintType: 'carbon' | 'hydric' | 'both';
  companyLogoUrl?: string;
}

const SharedResultsContent: React.FC<SharedResultsContentProps> = ({
  link,
  language,
  companyId,
  companyName,
  productName,
  year,
  footprintType,
  companyLogoUrl,
}) => {
  // Import components dynamically
  const [CarbonResults, setCarbonResults] = React.useState<any>(null);
  const [HydricResults, setHydricResults] = React.useState<any>(null);
  const [Footer, setFooter] = React.useState<any>(null);

  React.useEffect(() => {
    Promise.all([
      import('@/components/results/CarbonResults').then(m => setCarbonResults(() => m.CarbonResults)),
      import('@/components/results/HydricResults').then(m => setHydricResults(() => m.HydricResults)),
      import('@/components/layout/Footer').then(m => setFooter(() => m.Footer)),
    ]);
  }, []);

  if (!CarbonResults || !HydricResults || !Footer) {
    return <div>Loading...</div>;
  }

  // Translation helper
  const t = (key: string) => getTranslation(language, key);

  return (
    <div style={{
      width: '100%',
      padding: '48px',
      backgroundColor: '#ffffff',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxSizing: 'border-box'
    }}>
      {/* Header with Company Logo */}
      <div data-pdf-section="header" style={{ marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {companyLogoUrl ? (
            <div style={{
              flexShrink: 0,
              width: '100px',
              height: '100px',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              backgroundColor: '#ffffff',
              padding: '8px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={companyLogoUrl}
                alt={companyName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
          ) : (
            <div style={{
              flexShrink: 0,
              height: '100px',
              width: '100px',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <svg style={{ height: '50px', width: '50px', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '8px',
              margin: '0 0 8px 0',
              lineHeight: '1.3',
              letterSpacing: '0.02em',
              wordSpacing: '0.2em'
            }}>
              {t('sharedResults.title')}
            </h1>
            <p style={{
              color: '#6b7280',
              fontSize: '16px',
              margin: 0,
              lineHeight: '1.5',
              letterSpacing: '0.01em',
              wordSpacing: '0.15em'
            }}>
              {t('sharedResults.subtitle').replace('{companyName}', companyName)}
            </p>
          </div>
        </div>
      </div>

      {/* Company and Product Info - Simplified without Card */}
      <div data-pdf-section="company-info" style={{
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg style={{ height: '24px', width: '24px', color: '#6b7280', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>{t('pdf.company')}</p>
              <p style={{ fontWeight: '500', margin: 0, color: '#111827' }}>{companyName}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg style={{ height: '24px', width: '24px', color: '#6b7280', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>{t('pdf.product')}</p>
              <p style={{ fontWeight: '500', margin: 0, color: '#111827' }}>{productName}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg style={{ height: '24px', width: '24px', color: '#6b7280', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>{t('pdf.year')}</p>
              <p style={{ fontWeight: '500', margin: 0, color: '#111827' }}>{year}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Results - Use actual components */}
      <div style={{
        padding: '24px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        {(footprintType === 'carbon' || footprintType === 'both') && (
          <div style={{ marginBottom: footprintType === 'both' ? '48px' : '0' }}>
            <h3 data-pdf-section="carbon-title" style={{
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '24px',
              color: '#111827',
              margin: '0 0 24px 0'
            }}>
              {t('results.tabs.carbon')}
            </h3>
            <CarbonResults
              selectedProduct={productName}
              selectedYear={year.toString()}
              onProductChange={() => {}}
              onYearChange={() => {}}
              isSharedView={true}
              sharedCompanyId={companyId}
            />
          </div>
        )}

        {(footprintType === 'hydric' || footprintType === 'both') && (
          <div>
            <h3 data-pdf-section="hydric-title" style={{
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '24px',
              color: '#111827',
              margin: '0 0 24px 0'
            }}>
              {t('results.tabs.hydrics')}
            </h3>
            <HydricResults
              selectedProduct={productName}
              selectedYear={year.toString()}
              onProductChange={() => {}}
              onYearChange={() => {}}
              isSharedView={true}
              sharedCompanyId={companyId}
            />
          </div>
        )}
      </div>

      {/* Data responsibility / liability declaration */}
      <div data-pdf-section="data-responsibility" style={{
        marginTop: '32px',
        padding: '20px 24px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        <p style={{
          fontSize: '15px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 8px 0'
        }}>
          {t('pdf.dataResponsibilityTitle')}
        </p>
        <p style={{
          fontSize: '13px',
          color: '#4b5563',
          margin: 0,
          lineHeight: '1.6',
          textAlign: 'justify'
        }}>
          {t('pdf.dataResponsibilityContent')}
        </p>
      </div>

      {/* Footer */}
      <div data-pdf-section="footer" style={{ marginTop: '48px' }}>
        <Footer />
      </div>
    </div>
  );
};

/**
 * Capture the full SharedResults page as images and convert to PDF
 */
export async function captureFullPagePdf(
  link: SharedResultLink,
  language: 'en' | 'pt',
  companyLogoUrl?: string
): Promise<jsPDF> {
  console.log('[Full Page PDF] Starting full page capture...');

  // A4 dimensions at 150 DPI for better quality
  const pdfWidth = 1240; // 210mm at 150 DPI

  // Create temporary container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.zIndex = '-1';
  container.style.width = `${pdfWidth}px`;
  container.style.backgroundColor = '#ffffff';
  container.style.overflow = 'visible';
  document.body.appendChild(container);

  // Charts must render at final geometry immediately: their enter animation
  // runs on requestAnimationFrame, which Chrome freezes for occluded windows,
  // and a capture taken then would get empty pies/bars.
  setChartAnimationsDisabled(true);

  try {
    // Render the SharedResults page
    const root = createRoot(container);
    root.render(
      <LanguageProvider>
        <LanguageWrapper language={language}>
          <div data-page-id="shared-results-page" style={{ width: '100%', backgroundColor: '#ffffff' }}>
            <SharedResultsContent
              link={link}
              language={language}
              companyId={link.companyId}
              companyName={link.companyName}
              productName={link.productName}
              year={link.year}
              footprintType={link.footprintType}
              companyLogoUrl={companyLogoUrl}
            />
          </div>
        </LanguageWrapper>
      </LanguageProvider>
    );

    // Wait for rendering to complete (charts render only after lazy imports
    // and Firestore data resolve, so poll instead of using a blind delay)
    console.log('[Full Page PDF] Waiting for page to render...');
    await waitForChartsToRender(container);

    // Find the rendered page element
    const pageElement = container.querySelector('[data-page-id="shared-results-page"]') as HTMLElement;
    if (!pageElement) {
      throw new Error('Page element not found');
    }

    console.log(
      `[Full Page PDF] Capturing page as canvas... (chart shapes: ${
        pageElement.querySelectorAll('.recharts-sector, .recharts-bar-rectangle').length
      }, height: ${pageElement.scrollHeight}px)`
    );
    // Capture the entire page as canvas with higher quality settings
    const canvas = await html2canvas(pageElement, {
      scale: 1.5, // Balance quality vs. file size (avoids ~60 MB PDFs)
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: pdfWidth,
      width: pdfWidth,
      onclone: (clonedDoc) => {
        // Ensure all elements are visible in cloned document
        const clonedElement = clonedDoc.querySelector('[data-page-id="shared-results-page"]') as HTMLElement;
        if (clonedElement) {
          clonedElement.style.width = `${pdfWidth}px`;
          clonedElement.style.overflow = 'visible';
        }
      },
    });

    // Create PDF
    console.log('[Full Page PDF] Creating PDF document...');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;

    // Scale factor: how canvas pixels map to PDF mm
    const scaleFactor = canvas.width / pageWidth;
    // Page height in canvas pixels
    const pageHeightInPx = pageHeight * scaleFactor;
    // Small padding at top/bottom of each page (in canvas pixels)
    const pagePadding = 10 * scaleFactor; // 10mm padding

    // --- Content-aware section boundary detection ---
    // Query all data-pdf-section elements and get their Y positions in canvas coordinates
    const sectionElements = pageElement.querySelectorAll('[data-pdf-section]');
    const containerRect = pageElement.getBoundingClientRect();
    // Derive the DOM->canvas scale from the actual canvas instead of hardcoding
    // the html2canvas scale option; a stale constant here silently breaks every
    // section boundary and pages get sliced mid-content.
    const html2canvasScale = canvas.width / containerRect.width;

    // Build list of section boundaries (start Y, end Y in canvas pixels)
    const sectionBoundaries: { name: string; startY: number; endY: number }[] = [];
    sectionElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const startY = (rect.top - containerRect.top) * html2canvasScale;
      const endY = (rect.bottom - containerRect.top) * html2canvasScale;
      const name = el.getAttribute('data-pdf-section') || '';
      sectionBoundaries.push({ name, startY, endY });
    });

    console.log(`[Full Page PDF] Found ${sectionBoundaries.length} content sections`);

    // --- Greedy content-aware page slicing ---
    // Determine split points: we try to fit as much content as possible per page,
    // splitting between sections rather than through them.
    const slices: { sourceY: number; sourceHeight: number }[] = [];
    let currentY = 0;
    const totalCanvasHeight = canvas.height;

    while (currentY < totalCanvasHeight) {
      const availableHeight = pageHeightInPx - (2 * pagePadding);
      let idealEnd = currentY + availableHeight;

      if (idealEnd >= totalCanvasHeight) {
        // Last page - take everything remaining
        slices.push({ sourceY: currentY, sourceHeight: totalCanvasHeight - currentY });
        break;
      }

      // Find the best split point: the boundary between two sections that is
      // closest to (but not exceeding) idealEnd.
      let bestSplitY = idealEnd; // fallback: geometric split
      let foundSectionSplit = false;

      // Collect all section boundaries (endY values) that fall between currentY and idealEnd
      // We split AFTER a section ends (at its endY) so nothing gets cut
      for (const section of sectionBoundaries) {
        if (section.endY > currentY && section.endY <= idealEnd) {
          bestSplitY = section.endY;
          foundSectionSplit = true;
        }
      }

      // If no section boundary found within the page, check if a single section
      // exceeds page height (fallback to geometric split within that section)
      if (!foundSectionSplit) {
        // Check if there's a section that starts before idealEnd but ends after
        const oversizedSection = sectionBoundaries.find(
          (s) => s.startY <= currentY && s.endY > idealEnd
        );
        if (oversizedSection) {
          // Geometric fallback for oversized section
          bestSplitY = idealEnd;
        }
      }

      const sliceHeight = bestSplitY - currentY;
      if (sliceHeight <= 0) {
        // Safety: avoid infinite loop
        slices.push({ sourceY: currentY, sourceHeight: availableHeight });
        currentY += availableHeight;
        continue;
      }

      slices.push({ sourceY: currentY, sourceHeight: sliceHeight });
      currentY = bestSplitY;
    }

    console.log(`[Full Page PDF] Content-aware splitting into ${slices.length} PDF pages`);

    // Render each slice onto a PDF page
    for (let i = 0; i < slices.length; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      const { sourceY, sourceHeight } = slices[i];

      // Create a temporary canvas for this page slice
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      const tempCtx = tempCanvas.getContext('2d');

      if (tempCtx) {
        // White background
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        tempCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        // JPEG keeps each page slice small enough to email (pages render on white).
        const pageImage = tempCanvas.toDataURL('image/jpeg', 0.85);
        // Calculate the actual height this slice occupies in PDF mm
        const sliceHeightMm = sourceHeight / scaleFactor;
        // Center vertically with padding
        const yOffset = pagePadding / scaleFactor;
        pdf.addImage(pageImage, 'JPEG', 0, yOffset, imgWidth, sliceHeightMm, undefined, 'FAST');
      }
    }

    // Cleanup
    root.unmount();
    document.body.removeChild(container);

    console.log('[Full Page PDF] PDF generation complete');
    return pdf;
  } catch (error) {
    // Cleanup on error
    if (container.parentNode) {
      document.body.removeChild(container);
    }

    throw error;
  } finally {
    setChartAnimationsDisabled(false);
  }
}

/**
 * Export full page PDF
 */
export const fullPagePdfService = {
  exportToPdf: async (
    link: SharedResultLink,
    language: 'en' | 'pt',
    companyLogoUrl?: string
  ): Promise<void> => {
    const pdf = await captureFullPagePdf(link, language, companyLogoUrl);

    // Generate filename
    const cleanCompanyName = link.companyName.replace(/[^a-zA-Z0-9]/g, '_');
    const cleanProductName = link.productName.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${cleanCompanyName}_${cleanProductName}_${link.year}_footprint.pdf`;

    // Save the PDF
    pdf.save(filename);
  },

  getPdfBlob: async (
    link: SharedResultLink,
    language: 'en' | 'pt',
    companyLogoUrl?: string
  ): Promise<Blob> => {
    const pdf = await captureFullPagePdf(link, language, companyLogoUrl);
    return pdf.output('blob');
  },
};
